'use client'

import Link from 'next/link'
import { QuickSearchResponseRecord } from '@/search/types'
import { ProductThumbnail } from '@/General/ProductThumbnail'
import { resolveProductSearchThumbnailUrl } from '@/lib/search/resolveProductSearchThumbnailUrl'
import { resolveSearchDisplayPrice } from '@/lib/search/resolveSearchDisplayPrice'
import { useStorefrontMarket } from '@/useStorefrontMarket'
import { useCookieValue } from '@/Cookies/useCookies'
import {
  VAT_PREFERENCE_COOKIE,
  type VATPreference,
} from '@/vat/vatUtils'

interface ProductResultItemProps {
  result: QuickSearchResponseRecord
  isActive: boolean
  onClick: () => void
  onMouseEnter: () => void
  onMouseLeave: () => void
}

export const ProductResultItem = ({
  result,
  isActive,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: ProductResultItemProps) => {
  const { currencyCode, currencySymbol, countryIsoCode } = useStorefrontMarket()
  const vatPreference = useCookieValue(VAT_PREFERENCE_COOKIE) as VATPreference
  const { price, salePrice, hasDiscount } = resolveSearchDisplayPrice({
    result,
    currencyCode,
    countryIsoCode,
    vatPreference,
  })
  const displayPrice = hasDiscount ? salePrice : price
  const discountPercent =
    hasDiscount && price && salePrice
      ? Math.round(((price - salePrice) / price) * 100)
      : 0

  return (
    <Link
      href={result.productUrl}
      prefetch={false}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`flex items-center p-3 lg:p-2 border-b border-gray-100 last:border-b-0 transition-colors duration-150 touch-manipulation ${
        isActive ? 'bg-gray-100 lg:bg-gray-50' : 'hover:bg-gray-50 bg-white'
      }`}
    >
      {/* Thumbnail */}
      <div className="relative w-12 h-16 lg:w-10 lg:h-12 flex-shrink-0">
        <ProductThumbnail
          src={resolveProductSearchThumbnailUrl(result.thumbnailUrl, 60)}
          alt={result.title || 'Product'}
          className="w-full h-full object-cover rounded-lg"
          loading="lazy"
        />
        {result.inStock === 'false' && (
          <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
            <span className="text-white text-xs font-semibold">OOS</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="ml-3 lg:ml-3 flex-1 min-w-0">
        <div className="mb-1 flex flex-wrap items-center gap-1.5">
          <p className="min-w-0 text-sm lg:text-sm font-semibold text-gray-900 leading-tight line-clamp-2">
            {result.title}
          </p>
          {hasDiscount && (
            <span className="shrink-0 rounded bg-green-50 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-green-700 ring-1 ring-inset ring-green-200">
              {discountPercent > 0 ? `${discountPercent}% off` : 'On sale'}
            </span>
          )}
        </div>
        {result.creators?.trim() && (
          <p className="text-xs lg:text-xs text-gray-600 truncate">
            {result.creators}
          </p>
        )}
      </div>

      {/* Price */}
      {displayPrice !== null && displayPrice !== undefined && (
        <div className="ml-3 text-right flex-shrink-0">
          {displayPrice === 0 ? (
            <span className="text-sm font-semibold text-gray-900">Free</span>
          ) : (
            <span
              className={`text-sm font-semibold ${
                hasDiscount ? 'text-green-700' : 'text-gray-900'
              }`}
            >
              {result.options && result.options > 0 ? 'From ' : ''}
              {currencySymbol}
              {displayPrice.toFixed(2)}
            </span>
          )}
        </div>
      )}
    </Link>
  )
}
